// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import deleteLogo from "@bg/App/Handlers/CustomQuote/DeleteLogo";
import generatePdf from "@bg/App/Handlers/CustomQuote/GeneratePdf";
import setPdfSettingsDefaultValues from "@bg/App/Handlers/CustomQuote/SetPdfSettingsDefaultValues";
import uploadLogo from "@bg/App/Handlers/CustomQuote/UploadLogo";
import resetPdfSettings from "@bg/App/Handlers/PdfGenerator/ClearPdfSettings";
import updatePdfSettings from "@bg/App/Handlers/PdfGenerator/UpdatePdfSettings";
import { CustomQuoteModel, GroupLineModel } from "@bg/App/Services/CustomQuoteService";
import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { formatPhone, validateEmail, validatePhone } from "@bg/XDL/Utilities/inputValidation";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import translate from "@insite/client-framework/Translate";
import Accordion from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps, CheckboxStyle } from "@insite/mobius/Checkbox";
import DatePicker, { DatePickerState } from "@insite/mobius/DatePicker";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal from "@insite/mobius/Modal";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Tooltip from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    quote: CustomQuoteModel;
}

const mapStateToProps = (state: CustomAppState) => ({
    isLoading: state.myCustomQuoteDetails.isLoading,
    quoteId: state.myCustomQuoteDetails.quote?.id,
    logoName: state.myCustomQuoteDetails.quote?.logoFileName,
    customSettings: (getSettingsCollection(state) as any)?.customSettings,
    pdfSettings: state.pdfSettings.parameters,
    countries: getCurrentCountries(state),
    // get the images for each line on quote details page
    lineImages: state.commonActions["lineImages"],
    userEmail: getSession(state).email,
});

const mapDispatchToProps = {
    generatePdf,
    uploadLogo,
    updatePdfSettings,
    loadCurrentCountries,
    setPdfSettingsDefaultValues,
    resetPdfSettings,
    deleteLogo,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

enum PrintFormat {
    LineFormat = "lineFormat",
    Individual = "individual",
}

export interface MyListDetailsPDFStyles {
    accodrionTitle?: AccordionSectionPresentationProps & { initialExpanded: boolean };
    panelWrapper?: GridContainerProps;
    copyWrapper?: GridItemProps;
    copyText?: TypographyPresentationProps;
    uploadLogo?: FileUploadPresentationProps;
    logoName?: TypographyPresentationProps;
    logoWrapper?: InjectableCss;
    informationFieldsWrapper?: GridItemProps;
    printOptions?: GridItemProps;
    printOptinsLabel?: TypographyPresentationProps;
    taxOptions?: GridItemProps;
    bottomSectionWrapper?: GridItemProps;
    formFields?: TextFieldPresentationProps;
    selectField?: SelectPresentationProps;
    uploadLogoLabelWrapper?: InjectableCss;
    maxQuoteLogoSizeLabel?: TypographyPresentationProps;
    myLogoLabel?: TypographyPresentationProps;
    myLogoIcon?: IconPresentationProps;
    salesInputFieldsWrapper?: GridItemProps;
    printFormatWrapper?: InjectableCss;
    printFormatGroup?: RadioGroupProps;
    pdfActionsWrapper?: InjectableCss;
    buttonsWrapper?: InjectableCss;
    emailPdfButton?: ButtonPresentationProps;
    savePdfButton?: ButtonPresentationProps;
    viewPdfWrapper?: InjectableCss;
    viewPdfLink?: LinkPresentationProps;
    viewPdfText?: TypographyPresentationProps;
    notesTextArea?: TextAreaProps;
}

export const styles: MyListDetailsPDFStyles = {
    accodrionTitle: {
        initialExpanded: true,
        headerProps: {
            css: css`
                margin-top: 30px;
                button {
                    border: 0;
                    border-top: 2px solid ${getColor("common.ui3")};
                    background: transparent;
                    padding: 12px 0 !important;
                    &:focus {
                        border: 0;
                        border-top: 2px solid ${getColor("common.ui3")};
                    }

                    span {
                        font-size: 22px;
                        line-height: ${fontConfig.lineHeight.singleLineSmall};
                        letter-spacing: 0.22px;
                        font-weight: ${fontConfig.weight.semiBold};
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                border: 0;
                padding: 30px 30px 20px;
                overflow: hidden;
                background: ${getColor("common.ui4")};
                ${CheckboxStyle} {
                    margin-bottom: 15px;
                    span[role="checkbox"] {
                        background: ${getColor("common.white")};
                    }
                }

                && input + label:before {
                    background: ${getColor("common.white")};
                }
                input:hover:checked:not(:disabled) + label::before {
                    background: ${getColor("common.white")};
                    border-color: ${getColor("common.white")};
                }

                @media (max-width: 481px) {
                    padding: 20px;
                }
            `,
        },
    },
    panelWrapper: {
        gap: 40,
    },
    copyWrapper: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            margin-bottom: 20px;
            &&&&&.sales-copy {
                margin-bottom: 10px;
            }
        `,
    },
    copyText: {
        size: fontConfig.fontSize.regular,
        lineHeight: "20px",
        weight: fontConfig.weight.semiBold,
        css: css`
            display: block;
            @media (max-width: 600px) {
                width: 100%;
                margin-bottom: 10px;
            }
        `,
    },
    informationFieldsWrapper: {
        width: [12, 6, 6, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    printOptions: {
        width: [12, 6, 6, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    printOptinsLabel: {
        size: fontConfig.fontSize.regular,
        weight: fontConfig.weight.semiBold,
        css: css`
            margin-bottom: ${fontConfig.spacing.heading};
            display: block;
        `,
    },
    taxOptions: {
        width: [12, 6, 6, 4, 4],
        css: css`
            flex-direction: column;
            legend {
                font-weight: ${fontConfig.weight.semiBold};
            }
        `,
    },
    printFormatGroup: {
        css: css`
            legend {
                font-weight: ${fontConfig.weight.semiBold};
            }
        `,
    },
    bottomSectionWrapper: {
        width: 12,
        css: css`
            justify-content: flex-end;
            position: relative;
            margin-top: 10px !important;
            &:before {
                content: " ";
                position: absolute;
                top: 0;
                width: calc(100% + 40px);
                left: -20px;
                height: 1px;
                background: ${getColor("common.ui2")};
            }
            @media (max-width: 480px) {
                flex-wrap: wrap;
            }
        `,
    },

    formFields: {
        cssOverrides: {
            formField: css`
                width: 100%;
                label {
                    font-weight: ${fontConfig.weight.semiBold};
                }
                .address2 {
                    margin-top: 5px;
                }
            `,
            inputSelect: css`
                background: ${getColor("common.white")} !important;
            `,
        },
    },
    selectField: {
        mergeCss: true,
        cssOverrides: {
            formField: css`
                width: 100%;
                label {
                    font-weight: ${fontConfig.weight.semiBold};
                }
            `,
            inputSelect: css`
                background: ${getColor("common.white")} !important;
            `,
        },
    },
    logoWrapper: {
        css: css`
            width: 100%;
            padding-top: 22px;
            && > div[class*="UploadWrapper"] {
                flex-direction: row-reverse;
                background: ${getColor("common.white")};
                align-items: center;
                padding-left: 5px;
                position: relative;
            }
        `,
    },
    uploadLogo: {
        buttonProps: {
            variant: "secondary",
            css: css`
                margin: 0;
                padding: 0 20px;
                min-height: 32px;
                height: 32px;
                line-height: 29px;
                margin-right: 10px;
            `,
        },
        cssOverrides: {
            formField: css`
                && > span {
                    position: absolute;
                    top: -22px;
                    left: 4px;
                    font-weight: ${fontConfig.weight.semiBold};
                }
            `,
            formInputWrapper: css`
                margin: 0 !important;
                && > div[class*="FileUploadStyle"] {
                    padding: 0;
                    border: 0;
                    background: ${getColor("common.white")};
                }
            `,
        },
    },
    emailPdfButton: {
        sizeVariant: "small",
        css: css`
            padding: 0 12px;
            &&& svg {
                color: ${getColor("text")};
                transition: all 200ms ease-in-out;
            }

            &&:hover svg {
                color: ${getColor("common.white")};
            }

            @media (max-width: 481px) {
                width: 100%;
                margin: 0;
            }
        `,
        icon: {
            src: "Mail",
            position: "left",
        },
    },
    savePdfButton: {
        sizeVariant: "small",
        variant: "secondary",
        css: css`
            margin-left: 40px;
            padding: 0 12px;
            &&& svg {
                color: ${getColor("common.white")};
                transition: all 200ms ease-in-out;
            }

            &&:hover svg {
                color: ${getColor("text")};
            }
            @media (max-width: 481px) {
                width: 100%;
                margin: 0;
                margin-bottom: 15px;
            }
        `,
        icon: {
            src: "SavePdf",
            position: "left",
        },
    },
    viewPdfWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
        `,
    },
    viewPdfText: {
        variant: "h6",
        css: css`
            display: block;
            width: 100%;
        `,
    },
    uploadLogoLabelWrapper: {
        css: css`
            display: flex;
            gap: 8px;
        `,
    },
    maxQuoteLogoSizeLabel: {
        css: css`
            font-size: ${fontConfig.fontSize.xxSmall}px;
        `,
    },
    myLogoLabel: {
        css: css`
            display: flex;
            align-items: center;
            & > span {
                margin-left: 10px;
            }
        `,
    },
    viewPdfLink: linkIconStyleGenerator({
        icon: "Pdf",
        textSize: "15px",
        iconSize: 16,
    }),
    myLogoIcon: {
        src: "Info",
        color: "secondary",
        size: 13,
    },
    notesTextArea: {
        cssOverrides: {
            formField: css`
                label {
                    font-weight: ${fontConfig.weight.semiBold};
                }
            `,
            inputSelect: css`
                background: ${getColor("common.white")} !important;
                resize: none;
            `,
        },
    },
};

const PDFPrintSettings: React.FC<Props> = ({
    quote,
    generatePdf,
    toaster,
    quoteId,
    customSettings,
    uploadLogo,
    logoName,
    updatePdfSettings,
    pdfSettings,
    countries,
    loadCurrentCountries,
    isLoading,
    lineImages,
    userEmail,
    setPdfSettingsDefaultValues,
    resetPdfSettings,
    deleteLogo,
}) => {
    const [file, setFile] = useState<any>(null);
    const [clientEmail, setClientEmail] = useState("");
    const [clientEmailError, setClientEmailError] = useState("");
    const [website, setWebsite] = useState("");
    const [phone, setPhone] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [clientName, setClientName] = useState("");
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [freightCharges, setFreightCharges] = useState("0");
    const [labourCharges, setLabourCharges] = useState("0");
    const [tax, setTax] = useState("NoTax");
    const [displayPrice, setDisplayPrice] = useState(quote.priceDisplayType || "ShowProductPrices");

    const [specSheets, setSpecSheets] = useState(quote.withSpecSheet || pdfSettings["withSpecSheet"] || false);
    const [expirationDate, setExpirationDate] = useState(
        quote.expiryDate ? new Date(quote.expiryDate) : pdfSettings["ThirdPartyQuoteExpirationDate"] || null,
    );

    const [printFormat, setPrintFormat] = useState(
        quote.productsInLineFormat ? PrintFormat.LineFormat : PrintFormat.Individual,
    );
    const [loading, setLoading] = useState(false);
    const fileUploadRef = useRef({ value: "" } as HTMLInputElement);
    const [isPdfReady, setIsPdfReady] = useState(false);
    const [pdfUrl, setPdfUrl] = useState("");
    const [hasUpdatedLogo, setHasUpdatedLogo] = useState(false);
    const [quoteIdState, setQuoteIdState] = useState("");

    const [generalNotes, setGeneralNotes] = useState(pdfSettings["generalNotes"] || "");

    useEffect(() => {
        return () => {
            resetFields();
        };
    }, []);

    const resetFields = () => {
        setWebsite("");
        setPhone("");
        setClientName("");
        setAddress1("");
        setAddress2("");
        setCity("");
        setState("");
        setPostalCode("");
        setFreightCharges("0");
        setLabourCharges("0");
        setTax("NoTax");
        setExpirationDate(null);
        setSpecSheets(false);
        resetPdfSettings({});
        setQuoteIdState("");
    };

    const getQuoteLinesImagesWithId = () => {
        const quoteLines: GroupLineModel[] = mergeArrays(quote.groupLinesModel);
        const productsImages = {} as any;
        if (quote) {
            quoteLines.forEach(quoteLine => {
                quoteLine.productResult.images?.forEach(image => {
                    if (lineImages?.hasOwnProperty(quoteLine.productResult.id)) {
                        productsImages[quoteLine.productErpNumber] = "";
                        productsImages[quoteLine.productErpNumber] = lineImages[quoteLine.productResult.id].id;
                    } else if (quoteLine.productResult.images) {
                        const lineImage = quoteLine.productResult.images[0];
                        productsImages[quoteLine.productErpNumber] = "";
                        productsImages[quoteLine.productErpNumber] = lineImage.id;
                    }
                });
            });
        }
        return productsImages;
    };

    const [prevLineImages, setPrevLineImages] = useState();

    useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        }
    }, []);

    useEffect(() => {
        if (quoteIdState !== quote.id) {
            setQuoteIdState(quote.id);
            setWebsite(quote.website);
            setPhone(quote.phoneNumber);
            setClientName(quote.clientName);
            setAddress1(quote.streetAddress1);
            setAddress2(quote.streetAddress2);
            setCity(quote.city);
            setState(quote.province);
            setPostalCode(quote.postalCode);
            setFreightCharges(`${quote.freightCharges}`);
            setLabourCharges(`${quote.labourCharges}`);
            setTax(quote.taxDisplayType);
            setExpirationDate(quote.expiryDate ? new Date(quote.expiryDate) : null);
            setSpecSheets(quote.withSpecSheet || false);
            setClientEmail(quote.email || userEmail);
        }
    }, [quote]);

    useEffect(() => {
        setPdfUrl("");
        setTimeout(() => {
            updatePdfSettings({
                parameters: {
                    ThirdPartyQuoteClientEmail: clientEmail,
                    ThirdPartyQuoteWebsite: website,
                    ThirdPartyQuoteClientName: clientName,
                    ThirdPartyQuoteClientPhoneNumber: phone,
                    ThirdPartyQuoteClientStreetAddress1: address1,
                    ThirdPartyQuoteClientStreetAddress2: address2,
                    ThirdPartyQuoteClientCity: city,
                    ThirdPartyQuoteClientProvince: state,
                    ThirdPartyQuoteClientPostalCode: postalCode,
                    ThirdPartyQuoteFreightCharges: freightCharges,
                    ThirdPartyQuoteLabourCharges: labourCharges,
                    ThirdPartyQuoteTaxDisplayOption: tax,
                    ThirdPartyQuotePriceDisplayOption: displayPrice,
                    ThirdPartyQuoteExpirationDate: expirationDate,
                    pdfType: "ThirdPartyQuote",
                    withSpecSheet: specSheets,
                    productsInlineFormat: printFormat === PrintFormat.LineFormat,
                    projectName: quote?.name,
                    id: quote?.id,
                    generalNotes,
                },
            });
        }, 300);
    }, [
        clientEmail,
        website,
        clientName,
        address1,
        address2,
        city,
        state,
        postalCode,
        freightCharges,
        labourCharges,
        tax,
        displayPrice,
        expirationDate,
        specSheets,
        phone,
        printFormat,
        quote,
        generalNotes,
    ]);

    const addSpecSheetsHandler: CheckboxProps["onChange"] = (_, value) => {
        setSpecSheets(value);
    };

    const handleWebsiteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setWebsite(e.target.value);
    };

    const handleClientEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setClientEmailError("");
        setClientEmail(e.target.value);
    };

    const handleClientEmailBlur = (e: any) => {
        if (e.currentTarget.value) {
            const error = validateEmail(e.target.value);
            if (error) {
                setClientEmailError(error as string);
            } else {
                setClientEmailError("");
            }
        }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneError("");
        const number = formatPhone(e.target.value);
        setPhone(number);
    };

    const validatePhoneOnBlur = (e: any) => {
        if (e.currentTarget.value) {
            const error = validatePhone(e.currentTarget.value);
            if (error) {
                setPhoneError(error as string);
            } else {
                setPhoneError("");
            }
        }
    };

    const handleClientNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setClientName(e.target.value);
    };

    const handleAddress1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAddress1(e.target.value);
    };

    const handleAddress2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAddress2(e.target.value);
    };

    const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCity(e.target.value);
    };

    const handleStateChange = (e: React.FormEvent<HTMLSelectElement>) => {
        const state = e.currentTarget.value;
        setState(state);
    };

    const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPostalCode(e.target.value);
    };

    const handleFrieghtChargesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFreightCharges(e.target.value);
    };

    const handleLabourChargesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLabourCharges(e.target.value);
    };

    const handlePrintFormatSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as PrintFormat;
        setPrintFormat(value);
    };

    const handleTaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as "Hst" | "Gst" | "NoTax";
        setTax(value);
    };

    const handlePriceDisplayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as "ShowProductPrices" | "HideProductPrices";
        setDisplayPrice(value);
    };

    const expirationDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        setExpirationDate(selectedDay);
    };

    const handleGeneralNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setGeneralNotes(e.currentTarget.value);
    };

    const buildFormDataForTax = (uploadedFile: any) => {
        const formData = new FormData();
        formData.append(`file`, uploadedFile, uploadedFile.name || "");

        formData.append(`quoteId`, quoteId!);

        return formData;
    };

    const fileChangeHandler = (event: React.ChangeEvent<any>) => {
        const uploadedFile = event.target.files[0];
        // XNG-Changes: for not uploading file in Custom Quote details larger than maxQuoteLogoSize.
        if (
            uploadedFile &&
            customSettings.maxQuoteLogoSize &&
            uploadedFile.size > customSettings.maxQuoteLogoSize * 1024 * 1024
        ) {
            const maxSizeMB = customSettings.maxQuoteLogoSize;
            toaster.addToast({
                body: siteMessage("Custom_FileUpload_ExceedsMaximumSize", maxSizeMB),
                messageType: "danger",
            });
            return;
        }

        setFile(uploadedFile);
        if (uploadedFile) {
            const formData = buildFormDataForTax(uploadedFile);
            setLoading(true);
            uploadLogo({
                formData,
                customQuoteId: quote.id,
                onError: error => {
                    setLoading(false);
                    toaster.addToast({ body: error, messageType: "danger" });
                },
                onSuccess: result => {
                    setHasUpdatedLogo(true);
                    setLoading(false);
                    toaster.addToast({ body: translate("Logo Uploaded"), messageType: "success" });
                },
            });
        } else {
            setLoading(true);
            deleteLogo({
                customQuoteId: quote.id,
                onError: error => {
                    setLoading(false);
                    toaster.addToast({ body: error, messageType: "danger" });
                },
                onSuccess: result => {
                    setHasUpdatedLogo(true);
                    setLoading(false);
                    toaster.addToast({ body: translate("Logo Deleted"), messageType: "success" });
                },
            });
        }
    };

    function mergeArrays(obj: { [key: string]: any[] }) {
        let mergedArray: any[] = [];
        for (const key in obj) {
            mergedArray = mergedArray.concat(obj[key]);
        }
        return mergedArray;
    }

    const checkIfLineImagesNotChanged = () => {
        return JSON.stringify(prevLineImages) === JSON.stringify(lineImages);
    };

    const onEmail = () => {
        if (phone && phoneError) {
            return;
        }
        if (!checkIfLineImagesNotChanged()) {
            setPrevLineImages(lineImages);
        }

        setLoading(true);
        const productsImages = getQuoteLinesImagesWithId();
        generatePdf({
            id: quote.id,
            sendPdfByEmail: true,
            productsImages,
            onError: error => {
                if (error) {
                    setLoading(false);
                }
                if ("errorMessage" in error) {
                    toaster.addToast({ body: error.errorMessage, messageType: "danger" });
                } else {
                    toaster.addToast({ body: error, messageType: "danger" });
                }
            },

            onComplete: (result: any) => {
                if (result.successful) {
                    setLoading(false);
                    setHasUpdatedLogo(false);
                    toaster.addToast({ body: siteMessage("Custom_PDFEmailWillBeSent"), messageType: "success" });
                }
            },
        });
    };

    const saveDefaultValues = () => {
        setPdfSettingsDefaultValues({
            id: quote.id,
            onError: error => {
                if ("errorMessage" in error) {
                    toaster.addToast({ body: error.errorMessage, messageType: "danger" });
                } else {
                    toaster.addToast({ body: error, messageType: "danger" });
                }
            },
            onComplete: (result: any) => {
                if (result.apiResult?.successful) {
                    toaster.addToast({
                        body: translate("Current PDF setting has been defaulted"),
                        messageType: "success",
                    });
                } else {
                    toaster.addToast({
                        body: translate("An error occurred while setting defaults values"),
                        messageType: "danger",
                    });
                }
            },
        });
    };
    const onSave = () => {
        if (phone && phoneError) {
            return;
        }
        if (checkIfSettingsChanged()) {
            setIsPdfReady(true);
            return;
        }
        if (!checkIfLineImagesNotChanged()) {
            setPrevLineImages(lineImages);
        }
        setLoading(true);
        const productsImages = getQuoteLinesImagesWithId();
        generatePdf({
            id: quote.id,
            sendPdfByEmail: false,
            productsImages,
            onError: error => {
                if (error) {
                    setLoading(false);
                }
                if ("errorMessage" in error) {
                    toaster.addToast({ body: error.errorMessage, messageType: "danger" });
                } else {
                    toaster.addToast({ body: error, messageType: "danger" });
                }
            },
            onComplete: (result: any) => {
                if (result.successful) {
                    setLoading(false);
                }
                if (result.successful && result.result) {
                    const pdfLink = JSON.parse(result.result).pdfFileUrl;
                    if (pdfLink) {
                        toaster.addToast({ body: translate("PDF has been generated"), messageType: "success" });
                        setPdfUrl(pdfLink);
                        setIsPdfReady(true);
                        setHasUpdatedLogo(false);
                    } else {
                        toaster.addToast({
                            body: translate("An error occurred while generating the PDF"),
                            messageType: "danger",
                        });
                    }
                }
            },
        });
    };

    const checkIfSettingsChanged = () => {
        if (
            pdfUrl &&
            specSheets === pdfSettings["withSpecSheet"] &&
            (printFormat === PrintFormat.LineFormat) === pdfSettings["productsInlineFormat"] &&
            quote.name === pdfSettings["projectName"] &&
            phone === pdfSettings["ThirdPartyQuoteClientPhoneNumber"] &&
            quoteId === pdfSettings["id"] &&
            clientEmail === pdfSettings["ThirdPartyQuoteClientEmail"] &&
            clientName === pdfSettings["ThirdPartyQuoteClientName"] &&
            website === pdfSettings["ThirdPartyQuoteWebsite"] &&
            address1 === pdfSettings["ThirdPartyQuoteClientStreetAddress1"] &&
            address2 === pdfSettings["ThirdPartyQuoteClientStreetAddress2"] &&
            city === pdfSettings["ThirdPartyQuoteClientCity"] &&
            state === pdfSettings["ThirdPartyQuoteClientProvince"] &&
            postalCode === pdfSettings["ThirdPartyQuoteClientPostalCode"] &&
            tax === pdfSettings["ThirdPartyQuoteTaxDisplayOption"] &&
            displayPrice === pdfSettings["ThirdPartyQuotePriceDisplayOption"] &&
            freightCharges === pdfSettings["ThirdPartyQuoteFreightCharges"] &&
            labourCharges === pdfSettings["ThirdPartyQuoteLabourCharges"] &&
            expirationDate === pdfSettings["ThirdPartyQuoteExpirationDate"] &&
            !hasUpdatedLogo &&
            generalNotes === pdfSettings["generalNotes"] &&
            checkIfLineImagesNotChanged()
        ) {
            return true;
        }
        return false;
    };

    if (!customSettings) {
        return null;
    }

    const uploadLogoLabel = (
        <StyledWrapper {...styles.uploadLogoLabelWrapper}>
            <Typography {...styles.myLogoLabel}>
                {translate("My Logo")}
                <Tooltip
                    iconProps={styles.myLogoIcon}
                    text={translate(
                        customSettings.maxQuoteLogoSize
                            ? "Upload .JPG, .PNG, .BMP and .GIF with a max file size of {0} MB"
                            : "Upload .JPG, .PNG, .BMP and .GIF",
                        customSettings.maxQuoteLogoSize,
                    )}
                />
            </Typography>
            <Typography {...styles.maxQuoteLogoSizeLabel}>
                {customSettings.maxQuoteLogoSize} {translate("MB max size")}
            </Typography>
        </StyledWrapper>
    );

    return (
        <Accordion
            headingLevel={1}
            css={css`
                margin-bottom: 50px;
                @media print {
                    display: none;
                }
            `}
        >
            <ManagedAccordionSection {...styles.accodrionTitle} title={translate("PDF Display Settings")}>
                <LoadingOverlay loading={loading}>
                    <GridContainer {...styles.panelWrapper}>
                        <GridItem {...styles.informationFieldsWrapper}>
                            <GridContainer gap={20}>
                                <GridItem width={12}>
                                    <StyledWrapper {...styles.logoWrapper}>
                                        <FileUpload
                                            {...styles.uploadLogo}
                                            inputRef={fileUploadRef}
                                            fileName={file?.name || logoName}
                                            placeholder={translate(
                                                customSettings.maxQuoteLogoSize
                                                    ? "Upload .JPG, .PNG, .BMP and .GIF with a max file size of {0} MB"
                                                    : "Upload .JPG, .PNG, .BMP and .GIF",
                                                customSettings.maxQuoteLogoSize,
                                            )}
                                            accept={customSettings.allowedImageExtensions}
                                            buttonText={translate("Upload")}
                                            label={uploadLogoLabel}
                                            labelPosition="top"
                                            onFileChange={fileChangeHandler}
                                            uid="uploadLogoInput"
                                        />
                                    </StyledWrapper>
                                </GridItem>
                                <GridItem width={12}>
                                    <TextField
                                        {...styles.formFields}
                                        type="text"
                                        value={website || pdfSettings["ThirdPartyQuoteWebsite"]}
                                        onChange={handleWebsiteChange}
                                        maxLength={120}
                                        label={translate("My Website")}
                                        placeholder={translate("website URL")}
                                    />
                                </GridItem>

                                <GridItem width={12}>
                                    <TextField
                                        {...styles.formFields}
                                        type="text"
                                        value={clientEmail || pdfSettings["ThirdPartyQuoteClientEmail"]}
                                        onChange={handleClientEmailChange}
                                        onBlur={handleClientEmailBlur}
                                        error={clientEmailError}
                                        maxLength={120}
                                        label={translate("My Email")}
                                        placeholder={translate("Email@Address.com")}
                                    />
                                </GridItem>

                                <GridItem width={12}>
                                    <TextField
                                        {...styles.formFields}
                                        type="text"
                                        value={phone || pdfSettings["ThirdPartyQuoteClientPhoneNumber"]}
                                        maxLength={12}
                                        error={phoneError}
                                        onChange={handlePhoneChange}
                                        onBlur={validatePhoneOnBlur}
                                        label={translate("My Phone Number")}
                                        placeholder={translate("xxx-xxx-xxxx")}
                                    />
                                </GridItem>

                                <GridItem width={12}>
                                    <TextField
                                        {...styles.formFields}
                                        type="text"
                                        value={clientName || pdfSettings["ThirdPartyQuoteClientName"]}
                                        maxLength={50}
                                        onChange={handleClientNameChange}
                                        label={translate("Client Name")}
                                        placeholder={translate("Client's Name")}
                                    />
                                </GridItem>

                                <GridItem
                                    width={12}
                                    css={css`
                                        flex-wrap: wrap;
                                    `}
                                >
                                    <TextField
                                        {...styles.formFields}
                                        type="text"
                                        value={address1 || pdfSettings["ThirdPartyQuoteClientStreetAddress1"]}
                                        maxLength={50}
                                        onChange={handleAddress1Change}
                                        label={translate("Client Street Address")}
                                        placeholder={translate("Address Line 1")}
                                    />
                                    <TextField
                                        {...styles.formFields}
                                        className="address2"
                                        type="text"
                                        value={address2 || pdfSettings["ThirdPartyQuoteClientStreetAddress2"]}
                                        maxLength={50}
                                        onChange={handleAddress2Change}
                                        label={translate("")}
                                        placeholder={translate("Address Line 2")}
                                    />
                                </GridItem>

                                <GridItem width={12}>
                                    <TextField
                                        {...styles.formFields}
                                        type="text"
                                        value={city || pdfSettings["ThirdPartyQuoteClientCity"]}
                                        maxLength={50}
                                        onChange={handleCityChange}
                                        label={translate("Client City")}
                                        placeholder={translate("City Name")}
                                    />
                                </GridItem>
                                <GridItem width={12}>
                                    <GridContainer gap={20}>
                                        {countries && countries[0]?.states && countries[0].states.length > 0 && (
                                            <GridItem width={[12, 12, 6, 6, 6]}>
                                                <Select
                                                    value={state || pdfSettings["ThirdPartyQuoteClientProvince"]}
                                                    label={translate("Client Province")}
                                                    {...styles.selectField}
                                                    onChange={handleStateChange}
                                                >
                                                    <option value="">{translate("select")}</option>
                                                    {countries[0].states &&
                                                        countries[0].states.length > 0 &&
                                                        countries[0].states.map(o => (
                                                            <option key={o.abbreviation} value={o.abbreviation}>
                                                                {o.abbreviation}
                                                            </option>
                                                        ))}
                                                </Select>
                                            </GridItem>
                                        )}
                                        <GridItem width={[12, 12, 6, 6, 6]}>
                                            <TextField
                                                {...styles.formFields}
                                                type="text"
                                                value={postalCode || pdfSettings["ThirdPartyQuoteClientPostalCode"]}
                                                maxLength={50}
                                                onChange={handlePostalCodeChange}
                                                label={translate("Client Postal Code")}
                                                placeholder={translate("Postal Code")}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                            </GridContainer>
                        </GridItem>

                        <GridItem {...styles.taxOptions}>
                            <GridContainer gap={20}>
                                <GridItem width={12}>
                                    <TextField
                                        {...styles.formFields}
                                        type="number"
                                        value={freightCharges || pdfSettings["ThirdPartyQuoteFreightCharges"]}
                                        min={0}
                                        onChange={handleFrieghtChargesChange}
                                        maxLength={120}
                                        label={translate("Freight Charges")}
                                        placeholder={translate("$  0.00")}
                                    />
                                </GridItem>
                                <GridItem
                                    width={12}
                                    css={css`
                                        margin-bottom: 20px !important;
                                    `}
                                >
                                    <TextField
                                        {...styles.formFields}
                                        type="number"
                                        min={0}
                                        value={labourCharges || pdfSettings["ThirdPartyQuoteLabourCharges"]}
                                        onChange={handleLabourChargesChange}
                                        maxLength={120}
                                        label={translate("Labour Charges")}
                                        placeholder={translate("$  0.00")}
                                    />
                                </GridItem>
                                {/* XNG-Change BG-2417: disable tax options */}
                                {/* <GridItem
                                    width={12}
                                    css={css`
                                        margin-bottom: 20px !important;
                                    `}
                                >
                                    <RadioGroup
                                        label={translate("Tax Display Options")}
                                        {...styles.printFormatGroup}
                                        value={pdfSettings["ThirdPartyQuoteTaxDisplayOption"] || tax}
                                        horizontal
                                        onChangeHandler={handleTaxChange}
                                    >
                                        <Radio value="NoTax">{translate("No Tax")}</Radio>
                                        <Radio value="Gst">{translate("GST")}</Radio>
                                        <Radio value="Hst">{translate("HST")}</Radio>
                                    </RadioGroup>
                                </GridItem> */}
                                <GridItem
                                    width={12}
                                    css={css`
                                        margin-bottom: 20px !important;
                                    `}
                                >
                                    <RadioGroup
                                        label={translate("Price Display Options")}
                                        {...styles.printFormatGroup}
                                        value={pdfSettings["ThirdPartyQuotePriceDisplayOption"] || displayPrice}
                                        horizontal
                                        onChangeHandler={handlePriceDisplayChange}
                                    >
                                        <Radio value="ShowProductPrices">{translate("Show Product Prices")}</Radio>
                                        <Radio value="HideProductPrices">{translate("Hide Product Prices")}</Radio>
                                    </RadioGroup>
                                </GridItem>
                                <GridItem
                                    width={12}
                                    css={css`
                                        && > div,
                                        && > div > div {
                                            width: 100%;
                                            .react-datetime-picker {
                                                background: white !important;
                                            }
                                        }
                                    `}
                                >
                                    <DatePicker
                                        data-test-selector="customQuoteDetails_expirationDate"
                                        label={translate("Quote Expiration Date")}
                                        selectedDay={expirationDate}
                                        dateTimePickerProps={{
                                            minDate: new Date(),
                                            maxDate: undefined,
                                        }}
                                        onDayChange={expirationDateChangeHandler}
                                    />
                                </GridItem>
                                <GridItem width={12}>
                                    <TextArea
                                        {...styles.notesTextArea}
                                        value={generalNotes}
                                        maxLength={600}
                                        onChange={handleGeneralNotesChange}
                                        label={translate("Notes")}
                                        placeholder={translate("Type a Note")}
                                        rows={2}
                                    />
                                </GridItem>
                            </GridContainer>
                        </GridItem>

                        <GridItem {...styles.printOptions}>
                            <GridContainer>
                                <GridItem
                                    width={12}
                                    css={css`
                                        margin-bottom: 20px !important;
                                        flex-direction: column;
                                    `}
                                >
                                    <Typography {...styles.printOptinsLabel}>
                                        {translate("PDF Page Options")}
                                    </Typography>

                                    <Checkbox
                                        checked={specSheets}
                                        onChange={addSpecSheetsHandler}
                                        data-test-selector="addSpecSheets"
                                    >
                                        {translate("Spec Sheets")}
                                    </Checkbox>
                                </GridItem>
                                <GridItem
                                    width={12}
                                    css={css`
                                        margin-bottom: 20px !important;
                                    `}
                                >
                                    <RadioGroup
                                        label={translate("Which Options Would You Like to Include In the .PDF?")}
                                        {...styles.printFormatGroup}
                                        value={printFormat}
                                        onChangeHandler={handlePrintFormatSelect}
                                    >
                                        <Radio value="lineFormat">{translate("Products in line item format")}</Radio>
                                        <Radio value="individual">{translate("Products on individual pages")}</Radio>
                                    </RadioGroup>
                                </GridItem>
                            </GridContainer>
                        </GridItem>

                        <GridItem {...styles.bottomSectionWrapper}>
                            <Button onClick={() => saveDefaultValues()} {...styles.savePdfButton}>
                                {translate("Save PDF with default values")}
                            </Button>
                            <Button onClick={() => onSave()} {...styles.savePdfButton}>
                                {translate("Save PDF")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                    <Modal
                        size={500}
                        headline={translate("Your PDF is ready")}
                        isOpen={isPdfReady}
                        handleClose={() => setIsPdfReady(false)}
                    >
                        <StyledWrapper {...styles.viewPdfWrapper}>
                            <Typography {...styles.viewPdfText}>
                                {translate("Your PDF generation is done, click the link below:")}
                            </Typography>
                            <Link target="_blank" href={pdfUrl} {...styles.viewPdfLink}>
                                {translate("View PDF")}
                            </Link>
                        </StyledWrapper>
                    </Modal>
                </LoadingOverlay>
            </ManagedAccordionSection>
        </Accordion>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(PDFPrintSettings));
